import { SliderProps } from '~src/components/Slider/types';
import { useEnergyOptions } from '~src/context/EnergyOptionsContext';
import { usePublicCalculatorContext } from '~src/context/PublicCalculatorContext';
import { useOfferSavings } from '~src/hooks/useOfferSavings';
import { DeepPartial, Offer, Residence } from '~src/model';
import { findConstantForHeating } from '~src/util';

export const useAnnualConsumptionPrices = (offer?: Partial<Offer>, residence?: DeepPartial<Residence>) => {
  const { before, after } = useOfferSavings(offer, residence) ?? {};

  return {
    annualPriceBefore: before?.totalPrice ?? 0,
    annualPriceAfter: after?.totalPrice ?? 0,
  };
};

export const useConsumptionSliderProps = (
  residence?: DeepPartial<Residence>,
  updateResidence?: (input: DeepPartial<Residence>) => void
): SliderProps[] => {
  const { primaryHeating } = residence ?? {};

  const energyOptions = useEnergyOptions();

  const { fuelType = 'oil' } = primaryHeating ?? {};

  const constant = findConstantForHeating({ fuelType, ...primaryHeating }, energyOptions);

  const usageMap: Record<string, number> = {
    oil: 8000,
    gas: 7000,
    wood: 16,
    electricity: 15_000,
  };
  const { takeover } = usePublicCalculatorContext();

  const maxHeatingUsage = usageMap[fuelType] ?? 40_000;
  const maxElectricityUsage = 20e3;

  return [
    {
      value: primaryHeating?.annualUsage,
      label: 'Årligt varmeforbrug',
      onChange: (annualUsage?: number) =>
        updateResidence?.({ primaryHeating: { ...residence?.primaryHeating, annualUsage: annualUsage ?? 0 } }),
      unit: constant?.denominator ?? 'm3',
      max: maxHeatingUsage,
      withInputField: true,
      disabled: takeover === 'invalidHeating' ? true : false,
      tooltip: { formatter: number => (number === maxHeatingUsage ? `Højere end ${number}` : number) },
      status: takeover === 'highUsage' ? 'warning' : undefined,
    },
    {
      value: residence?.energyExpenditure,
      label: 'Årligt elforbrug',
      onChange: (energyExpenditure?: number) => updateResidence?.({ energyExpenditure }),
      unit: 'kWh',
      max: maxElectricityUsage,
      withInputField: true,
      stepSize: 10,
      tooltip: { formatter: number => (number === maxElectricityUsage ? `Højere end ${number}` : number) },
    },
  ];
};
